/**
 * Carries the book idea a visitor typed on the homepage through signup and
 * into the writer.
 *
 * The hero input is a live demo: someone types their actual concept, watches a
 * sample chapter generate, then clicks "Start Writing". Before this, signing up
 * dropped that text on the floor and dumped them in an empty writer — asking
 * them to retype the idea they had just given us, at the exact moment they were
 * most willing to start.
 *
 * localStorage rather than sessionStorage because Google OAuth leaves the site
 * entirely and comes back through /auth/callback; sessionStorage survives that
 * in most browsers, but not reliably enough to bet the flow on.
 *
 * The writer already accepts ?concept= (it was built for the SEO micro-tool
 * landing pages), so consuming this just means appending a query param.
 */

const KEY = 'pending_concept';
const TTL_MS = 60 * 60 * 1000; // 1 hour — this is one sitting, not a campaign
const MAX_LEN = 600; // matches the writer's own concept field cap

interface Stored {
  text: string;
  at: number;
}

/** Called when a logged-out visitor heads for signup with an idea typed. */
export function stashConcept(text: string): void {
  const trimmed = (text || '').trim();
  if (!trimmed) return;
  try {
    const payload: Stored = { text: trimmed.slice(0, MAX_LEN), at: Date.now() };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    // Private mode / storage disabled — the flow still works, just without
    // the prefill. Never let this break signup.
  }
}

/**
 * Reads and clears the stashed idea. Single-use by design: a stale concept
 * resurfacing on a later signup in a shared browser would be worse than none.
 */
export function takeConcept(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    localStorage.removeItem(KEY);
    const parsed: Stored = JSON.parse(raw);
    if (!parsed || typeof parsed.text !== 'string') return null;
    if (Date.now() - parsed.at > TTL_MS) return null;
    return parsed.text || null;
  } catch {
    return null;
  }
}

/**
 * The post-signup destination: the writer, prefilled if we have an idea.
 * Used by both the email and Google signup paths so they cannot drift apart.
 */
export function writerDestination(): string {
  const concept = takeConcept();
  return concept ? `/bookwriter?concept=${encodeURIComponent(concept)}` : '/bookwriter';
}
